import * as Sentry from '@sentry/react';

import { processSentryEvent } from '../utils/process-sentry-event';
import { setSentryUser, clearSentryUser } from '../utils/sentry-user';

export { setSentryUser, clearSentryUser };

interface SentryInitOptions<EnvType extends string> {
  dsn: string;
  environment: EnvType;
  sessionId?: string;
  sessionIdTagKey?: string;
  tags?: Record<string, string>;
  shouldCaptureConsoleErrors?: boolean;
  userId?: string;
}

export const initializeSentry = <EnvType extends string>({
  dsn,
  environment,
  sessionId,
  sessionIdTagKey = 'session_id',
  tags,
  shouldCaptureConsoleErrors = true,
  userId,
}: SentryInitOptions<EnvType>) => {
  // Identity must be on the current scope BEFORE init: browserSessionIntegration starts the
  // initial session synchronously during Sentry.init() and seeds its `did` from the current
  // scope's user. Setting the user after init leaves that first session (and the metric) anonymous.
  if (userId) {
    setSentryUser(userId);
  }

  Sentry.init({
    dsn,
    environment,
    integrations: shouldCaptureConsoleErrors ? [Sentry.captureConsoleIntegration({ levels: ['error'] })] : [],
    beforeSend: processSentryEvent,
  });

  // Set session ID tag for cross-project correlation
  if (sessionId) {
    Sentry.setTag(sessionIdTagKey, sessionId);
  }

  // Set additional tags
  if (tags) {
    Object.entries(tags).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }

  // Add online/offline breadcrumbs for network diagnostics
  if (typeof window !== 'undefined') {
    window.addEventListener('offline', () => {
      Sentry.addBreadcrumb({ category: 'network', message: 'Device went offline', level: 'warning', data: { online: false } });
    });
    window.addEventListener('online', () => {
      Sentry.addBreadcrumb({ category: 'network', message: 'Device came back online', level: 'info', data: { online: true } });
    });
  }
};

export const setSentryTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

export const setSentryContext = (name: string, context: Record<string, unknown> | null) => {
  Sentry.setContext(name, context);
};

export const captureSentryException = (error: Error | unknown, context?: Record<string, unknown>) => {
  Sentry.captureException(error, { extra: context });
};

export const captureSentryMessage = (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, unknown>) => {
  Sentry.captureMessage(message, { level, extra: context });
};
